import { t } from "i18next";
import { formatLocalDate } from "../../../services/export/timezoneUtils";
import ReplenishmentStatusBadge from "../../../components/data/replenishment-status/ReplenishmentStatusBadge";
import ReplenishmentPriorityBadge from "../../../components/data/replenishment-priority/ReplenishmentPriorityBadge";
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import { useState, useEffect } from "react";

interface ReplenishmentsTableProps {
  replenishments: EasyTech.Replenishment[];
  onHeadClick: (head: string, orderDesc: boolean) => void;
  selectedHead: string;
  orderDesc: boolean;
}

/**
 * Tableau des demandes de pièces avec tri et navigation
 * @param replenishments - Liste des demandes
 * @param onHeadClick - Callback lors du clic sur un en-tête
 * @param selectedHead - En-tête actuellement trié
 * @param orderDesc - Ordre décroissant
 */
export default function ReplenishmentsTable({
  replenishments,
  onHeadClick,
  selectedHead,
  orderDesc
}: ReplenishmentsTableProps) {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1440);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 1440);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tableHead = [
    { label: t("replenishment:fields.ref"), orderKey: "ref", width: "10%" },
    { label: t("replenishment:fields.created"), orderKey: "created", width: "10%" },
    { label: t("replenishment:fields.requestFrom"), orderKey: "requestFrom", width: "10%" },
    { label: t("replenishment:fields.createdBy"), orderKey: "technician", width: "10%" },
    { label: t("replenishment:fields.sentTo"), orderKey: "assignedTechnician", width: "15%" },
    { label: t("replenishment:fields.vanToRestock"), orderKey: "van", width: "15%" },
    { label: t("replenishment:fields.status"), orderKey: "status", width: isSmallScreen ? "80px" : "10%", headClassName: isSmallScreen ? "text-center" : "" },
    { label: t("replenishment:fields.updated"), orderKey: "updated", width: "10%" },
    { label: t("replenishment:fields.priority"), orderKey: "priority", width: isSmallScreen ? "80px" : "10%", headClassName: isSmallScreen ? "text-center" : "" }
  ];

  return (
    <Table 
      tableHead={tableHead}
      onHeadClick={onHeadClick}
      selectedHead={selectedHead}
      orderDesc={orderDesc}
      enableRowLinks={true}
    >
      {replenishments.map((replenishment, index) => {
        const isLast = index === replenishments.length - 1;
        const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";
        const replenishmentUrl = `/replenishments/${replenishment.id}`;
        const refLabel = replenishment.ref || `#${replenishment.id.slice(0, 8)}`;
        const requestFromLabel = replenishment.requestFrom
          ? `${replenishment.requestFrom.firstName} ${replenishment.requestFrom.lastName}`.trim()
          : "";
        const technicianLabel = replenishment.technician
          ? `${replenishment.technician.firstName} ${replenishment.technician.lastName}`.trim()
          : "";
        const assignedTechnicianLabel = replenishment.assignedTechnician
          ? `${replenishment.assignedTechnician.firstName} ${replenishment.assignedTechnician.lastName}`.trim()
          : "";
        
        return (
          <tr key={replenishment.id} data-href={replenishmentUrl}>
            {/* Ref */}
            <td className={classes}>
              <OverflowTooltip content={refLabel} className="et-cell-truncate text-sm font-medium">
                <span>{refLabel}</span>
              </OverflowTooltip>
            </td>
            
            {/* Created on */}
            <td className={classes}>
              <span className="et-cell-wrap text-sm">
                {replenishment.created 
                  ? formatLocalDate(new Date(replenishment.created).toISOString(), localStorage.getItem("realm") || "default")
                  : ""
                }
              </span>
            </td>

            {/* Created for */}
            <td className={classes}>
              <OverflowTooltip content={requestFromLabel} className="et-cell-truncate text-sm">
                <span>{requestFromLabel}</span>
              </OverflowTooltip>
            </td>
            
            
            {/* Created by */}
            <td className={classes}>
              <OverflowTooltip content={technicianLabel} className="et-cell-truncate text-sm">
                <span>{technicianLabel}</span>
              </OverflowTooltip>
            </td>
            
            {/* Sent to */}
            <td className={classes}>
              <OverflowTooltip content={assignedTechnicianLabel} className="et-cell-truncate text-sm">
                <span>{assignedTechnicianLabel}</span>
              </OverflowTooltip>
            </td>
            
            {/* Van */}
            <td className={classes}>
              <div className="et-cell-wrap text-sm">
                {replenishment.van ? replenishment.van.name : ""}
              </div>
            </td>
            
            {/* Status */}
            <td className={classes}>
              <div className={isSmallScreen ? "et-cell-centered" : ""}>
                <ReplenishmentStatusBadge status={replenishment.status} />
              </div>
            </td>
            
            {/* Updated on */}
            <td className={classes}>
              {(() => {
                const dateString = replenishment.updated
                  ? formatLocalDate(new Date(replenishment.updated).toISOString(), localStorage.getItem("realm") || "default")
                  : "";
                return (
                  <div className="et-cell-wrap text-sm">
                    {dateString}
                  </div>
                );
              })()}
            </td>
            
            {/* Priority */}
            <td className={classes}>
              <div className={isSmallScreen ? "et-cell-centered" : ""}>
                <ReplenishmentPriorityBadge priority={replenishment.priority} iconOnly={isSmallScreen} />
              </div>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}
